import { MobileSheet } from "@/components/ui/mobile-sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  useCitationIsMobile,
  useCitationSources,
} from "@/contexts/citation-sources-context";
import { useTranslation } from "@/hooks/useTranslation";
import {
  resolveCitationSources,
  type CitationSource,
} from "@/lib/citation-sources";
import { cn } from "@/lib/utils";
import { ExternalLinkIcon } from "lucide-react";
import { useState, type ComponentProps, type ReactElement } from "react";
import { SourceFavicon } from "./source-favicon";

type CitationPillProps = {
  /** The ids the marker cited, space separated. */
  readonly ids?: string;
};

/** The class that writes a pill's label: see `CitationPillButton`. */
const CITATION_LABEL_CLASS = "before:content-[attr(data-label)]";

/**
 * The pill itself. Its label is written by CSS from `data-label`, not as
 * text: a copy of the answer (a selection, Streamdown's copy buttons) walks the
 * text nodes of the page and must not pick it up.
 *
 * A grey filled chip with the site's logo, the way ChatGPT shows its sources: seen
 * without pulling the eye from the text, and never taken for a company chip,
 * which is in the brand colour. The fill is the text colour at a low opacity
 * rather than `bg-muted`, which is nearly the chat's background and left the
 * pill invisible. Twenty pixels tall in the line; on a touch screen a transparent zone around
 * it widens the target to the whole height of its own line, and no further. A
 * 44 px zone would reach ten pixels into the lines above and below, and the
 * zone is tappable: a finger aiming at a word, a link or a company chip there
 * opened the sheet instead. An inline target cannot be 44 px high without
 * taking taps from its neighbours or pushing the lines apart — the case WCAG
 * 2.5.8 exempts for targets inside a sentence.
 */
function CitationPillButton({
  sources,
  className,
  ...props
}: ComponentProps<"button"> & {
  readonly sources: ReadonlyArray<CitationSource>;
}) {
  const { t } = useTranslation();
  const [first] = sources;
  const extra = sources.length - 1;

  return (
    <button
      type="button"
      aria-label={t("chat.message.citations.open", {
        count: sources.length,
        site: first.host,
      })}
      className={cn(
        "relative mx-1 inline-flex max-w-[160px] cursor-pointer select-none items-center gap-1 rounded-full px-2 py-0.5",
        "align-baseline text-[11px] font-medium leading-4 no-underline transition-colors",
        "bg-foreground/[0.07] text-muted-foreground",
        "hover:bg-foreground/[0.12] hover:text-foreground data-[state=open]:bg-foreground/[0.12] data-[state=open]:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-majorelle/40",
        "pointer-coarse:after:absolute pointer-coarse:after:-inset-x-1 pointer-coarse:after:-inset-y-0.5",
        className,
      )}
      {...props}
    >
      <SourceFavicon host={first.host} />
      <span
        aria-hidden="true"
        data-label={first.host}
        className={cn("min-w-0 truncate", CITATION_LABEL_CLASS)}
      />
      {extra > 0 && (
        <span
          aria-hidden="true"
          data-label={`+${extra}`}
          className={cn("shrink-0 opacity-70", CITATION_LABEL_CLASS)}
        />
      )}
    </button>
  );
}

/** Each source of the pill: its name linking out, its site, the passage used. */
function CitationSourceList({
  sources,
}: {
  readonly sources: ReadonlyArray<CitationSource>;
}) {
  return (
    <div className="space-y-3">
      {sources.map((source) => (
        <div key={source.id} className="space-y-1 text-sm">
          <a
            href={source.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-1.5 font-medium text-foreground hover:underline"
          >
            <span className="min-w-0 break-words">{source.title}</span>
            <ExternalLinkIcon className="mt-1 size-3 shrink-0 text-muted-foreground" />
          </a>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <SourceFavicon host={source.host} />
            <span className="truncate">{source.host}</span>
          </div>
          {source.snippet && (
            <p className="line-clamp-4 text-xs text-muted-foreground">
              {source.snippet}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A trigger that opens a list of sources on a tap — a click rather than a
 * hover, so a phone reaches it too. A popover on desktop, a bottom sheet on
 * mobile. `renderTrigger` draws the button, given the click and the
 * `data-state` of the list on a phone; on desktop, Radix adds them.
 */
export function CitationSourcesPreview({
  sources,
  isMobile,
  renderTrigger,
}: {
  readonly sources: ReadonlyArray<CitationSource>;
  readonly isMobile: boolean;
  readonly renderTrigger: (
    props: Pick<ComponentProps<"button">, "onClick"> & {
      readonly "data-state"?: "open" | "closed";
    },
  ) => ReactElement;
}) {
  const { t } = useTranslation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // An answer carries a pill per paragraph and the reader opens one or two: a
  // sheet is mounted on its pill's first tap, then kept so it can animate out.
  const [hasSheet, setHasSheet] = useState(false);

  if (isMobile) {
    return (
      <>
        {/* The open look the popover trigger gets from Radix. */}
        {renderTrigger({
          "data-state": isSheetOpen ? "open" : "closed",
          onClick: () => {
            setHasSheet(true);
            setIsSheetOpen(true);
          },
        })}
        {hasSheet && (
          <MobileSheet
            open={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            heightClass="h-auto max-h-[80dvh]"
          >
            <SheetTitle className="sr-only">
              {t("chat.message.citations.recap", { count: sources.length })}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t("chat.message.citations.sheetDescription")}
            </SheetDescription>
            <div className="overflow-y-auto overscroll-y-contain px-4 pt-1 pb-4">
              <CitationSourceList sources={sources} />
            </div>
          </MobileSheet>
        )}
      </>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{renderTrigger({})}</PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        className="w-80 max-w-[calc(100vw-24px)] p-3"
      >
        <CitationSourceList sources={sources} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The pill a citation marker renders as: the first source's site, `+N` when the
 * sentence rests on several, and a list of them on a tap.
 *
 * Each id is resolved against the pages the conversation searched. An id no
 * search returned renders nothing — a citation is shown only when its source
 * is real — and a marker none of whose ids resolve leaves no pill at all.
 * Streamdown hands a custom tag's attributes over as props, whatever they say:
 * the ids are read as text, never as markup.
 */
export function CitationPill({ ids }: CitationPillProps) {
  const isMobile = useCitationIsMobile();
  const known = useCitationSources();
  const sources = resolveCitationSources(
    typeof ids === "string" ? ids.split(/\s+/).filter(Boolean) : [],
    known,
  );
  if (sources.length === 0) return null;

  return (
    <CitationSourcesPreview
      sources={sources}
      isMobile={isMobile}
      renderTrigger={(props) => (
        <CitationPillButton sources={sources} {...props} />
      )}
    />
  );
}
